import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, ChevronDown } from 'lucide-react';
import TrustBar from './TrustBar';
import LogoVideo from './LogoVideo';

const langs = [
  { code: 'en' as const, label: 'EN' },
  { code: 'uk' as const, label: 'UA' },
  { code: 'ja' as const, label: 'JP' },
];

const navItems = [
  { key: 'story', href: '#story' },
  { key: 'howItWorks', href: '#how-it-works' },
] as const;

const navItemsTail = [
  { key: 'impact', href: '#story' },
  { key: 'invest', href: '/invest' },
  { key: 'contact', href: '#contact' },
] as const;

const agents = [
  { name: 'Dr.Vet Voice Agent', path: '/voice-agent' },
  { name: 'Dr.Vet Call Intelligence', path: '/call-intelligence' },
  { name: 'Dr.Vet Medical Scribe', path: '/medical-scribe' },
  { name: 'Dr.Vet X-Ray Intelligence', path: '/x-ray-intelligence' },
  { name: 'Dr.Vet Knowledge Core', path: '/knowledge-core' },
  { name: 'Dr.Vet Chief Auditor', path: '/chief-auditor' },
  { name: 'Dr.Vet Reputation Shield', path: '/reputation-shield' },
  { name: 'SaveVets_SafePets', path: '/savevets-safepets' },
  { name: 'Dr.Vet Idea Incubator', path: '/idea-incubator' },
];

const labels = {
  en: { agents: 'Our Agents', impact: 'Impact' },
  uk: { agents: 'Наші агенти', impact: 'Вплив' },
  ja: { agents: 'AIエージェント', impact: 'インパクト' },
};

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [agentsOpen, setAgentsOpen] = useState(false);
  const [mobileAgentsOpen, setMobileAgentsOpen] = useState(false);
  const L = labels[lang];

  const langSuffix = lang === 'en' ? '' : `?lang=${lang}`;

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border/50"
      >
        <div className="container-max flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16 md:h-20">
          <a href="#" className="flex items-center gap-2">
            <LogoVideo />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {navItems.map((n) => (
              <a
                key={n.key}
                href={n.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {t.nav[n.key]}
              </a>
            ))}

            {/* Our Agents dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setAgentsOpen(true)}
              onMouseLeave={() => setAgentsOpen(false)}
            >
              <button
                onClick={() => setAgentsOpen(v => !v)}
                className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                aria-expanded={agentsOpen}
              >
                {L.agents}
                <ChevronDown size={14} className={`transition-transform ${agentsOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {agentsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-2"
                  >
                    <div className="w-64 rounded-lg border border-border bg-background shadow-lg py-2">
                      {agents.map((a) => (
                        <a
                          key={a.path}
                          href={`${a.path}${langSuffix}`}
                          className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        >
                          {a.name}
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a
              href="#story"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {L.impact}
            </a>
            {navItemsTail.slice(1).map((n) => (
              <a
                key={n.key}
                href={n.key === 'invest' ? `/invest${langSuffix}` : n.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {t.nav[n.key as 'invest' | 'contact']}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground transition-colors"
              aria-label={theme === 'light' ? t.theme.dark : t.theme.light}
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>

            <div className="flex items-center rounded-lg border border-border overflow-hidden">
              {langs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    lang === l.code ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <button
              className="lg:hidden p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <div className="space-y-1.5">
                <span className={`block w-6 h-0.5 bg-foreground transition-transform ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <span className={`block w-6 h-0.5 bg-foreground transition-opacity ${mobileOpen ? 'opacity-0' : ''}`} />
                <span className={`block w-6 h-0.5 bg-foreground transition-transform ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="lg:hidden bg-background border-b border-border"
          >
            <nav className="flex flex-col px-6 py-4 gap-1">
              {navItems.map((n) => (
                <a
                  key={n.key}
                  href={n.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground py-2"
                >
                  {t.nav[n.key]}
                </a>
              ))}

              <button
                onClick={() => setMobileAgentsOpen(v => !v)}
                className="flex items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground py-2"
              >
                {L.agents}
                <ChevronDown size={14} className={`transition-transform ${mobileAgentsOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileAgentsOpen && (
                <div className="pl-3 border-l border-border ml-1 mb-1">
                  {agents.map(a => (
                    <a
                      key={a.path}
                      href={`${a.path}${langSuffix}`}
                      onClick={() => setMobileOpen(false)}
                      className="block py-1.5 text-sm text-muted-foreground hover:text-foreground"
                    >
                      {a.name}
                    </a>
                  ))}
                </div>
              )}

              <a href="#story" onClick={() => setMobileOpen(false)} className="text-sm font-medium text-muted-foreground hover:text-foreground py-2">{L.impact}</a>
              <a href={`/invest${langSuffix}`} onClick={() => setMobileOpen(false)} className="text-sm font-medium text-muted-foreground hover:text-foreground py-2">{t.nav.invest}</a>
              <a href="#contact" onClick={() => setMobileOpen(false)} className="text-sm font-medium text-muted-foreground hover:text-foreground py-2">{t.nav.contact}</a>
            </nav>
          </motion.div>
        )}
      </motion.header>

      {/* Trust Bar — static, sits directly below the fixed header (offset by header height) */}
      <div className="pt-16 md:pt-20">
        <TrustBar />
      </div>
    </>
  );
}
